import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { Plus, X, AlertTriangle, Eye, Wallet, TrendingUp, CalendarClock, Hourglass } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card, StatCard } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { leaveService } from '@/services';
import { fmtDate, fmtTime12 } from '@/utils/format';

export default function Leaves() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [cancelling, setCancelling] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['leaves', 'mine', page],
    queryFn: () => leaveService.mine({ page, limit: 10 }),
  });

  const { data: bal } = useQuery({
    queryKey: ['leaves', 'balance'],
    queryFn: () => leaveService.balance(),
  });

  const balance = bal?.balance ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Leaves"
        subtitle="Apply for and track your leave requests"
        actions={
          <Link to="/leaves/new" className="btn-primary">
            <Plus size={16} /> Apply for leave
          </Link>
        }
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Wallet}
          color={balance < 0 ? 'rose' : 'emerald'}
          label="Available balance"
          value={balance < 0 ? `${balance} (owed)` : `${balance} left`}
          hint="Paid leaves you can take"
        />
        <StatCard
          icon={TrendingUp}
          color="brand"
          label="Auto-credit"
          value={`+${bal?.accrualPerMonth ?? 1}/mo`}
          hint="Added on the 1st each month"
        />
        <StatCard
          icon={CalendarClock}
          color="violet"
          label="Used this year"
          value={`${bal?.usedThisYear ?? 0} days`}
          hint={`Approved paid leaves in ${dayjs().year()}`}
        />
        <StatCard
          icon={Hourglass}
          color="amber"
          label="Pending"
          value={`${bal?.pending ?? 0}`}
          hint="Awaiting admin review"
        />
      </div>

      <Card>
        {isLoading ? (
          <Loader />
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Type</th>
                  <th className="th">Date / Range</th>
                  <th className="th">Duration</th>
                  <th className="th">Reason</th>
                  <th className="th">Status</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).length === 0 && (
                  <tr><td colSpan={6} className="td text-center text-slate-500">No leave requests yet</td></tr>
                )}
                {(data?.items || []).map((l) => {
                  const cancellable = l.status === 'pending' || l.status === 'approved';
                  return (
                    <tr
                      key={l._id}
                      onClick={() => navigate(`/leaves/${l._id}`)}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="td capitalize">
                        {l.type === 'short' ? 'Short leave' : l.type}
                      </td>
                      <td className="td">
                        {l.type === 'short' ? (
                          <>
                            <div>{fmtDate(l.startDate)}</div>
                            <div className="text-xs text-slate-500">{fmtTime12(l.startTime)} – {fmtTime12(l.endTime)}</div>
                          </>
                        ) : (
                          <>
                            <div>{fmtDate(l.startDate)}</div>
                            {dayjs(l.startDate).format('YYYY-MM-DD') !== dayjs(l.endDate).format('YYYY-MM-DD') && (
                              <div className="text-xs text-slate-500">to {fmtDate(l.endDate)}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="td">
                        {l.type === 'short'
                          ? `${(l.hours || 0).toFixed(2)} hrs`
                          : `${l.days} day${l.days === 1 ? '' : 's'}`}
                      </td>
                      <td className="td max-w-xs">
                        <div className="truncate" title={l.reason}>{l.reason}</div>
                        {l.status === 'cancelled' && l.cancellationReason && (
                          <div className="text-[11px] text-rose-500 mt-0.5 truncate" title={l.cancellationReason}>
                            Cancelled: {l.cancellationReason}
                          </div>
                        )}
                      </td>
                      <td className="td"><StatusBadge status={l.status} /></td>
                      <td className="td" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/leaves/${l._id}`)}
                            className="btn-ghost p-1 text-slate-500 hover:text-brand-600"
                            title="View details"
                          >
                            <Eye size={14} />
                          </button>
                          {cancellable ? (
                            <button
                              onClick={() => setCancelling(l)}
                              className="btn-ghost text-rose-600"
                              title="Cancel leave"
                            >
                              <X size={14} /> Cancel
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} limit={10} total={data?.total || 0} onChange={setPage} />
      </Card>

      {cancelling && (
        <CancelLeaveModal
          leave={cancelling}
          onClose={() => setCancelling(null)}
          onConfirmed={() => {
            setCancelling(null);
            qc.invalidateQueries({ queryKey: ['leaves'] });
          }}
        />
      )}
    </div>
  );
}

function CancelLeaveModal({ leave, onClose, onConfirmed }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const wasApproved = leave.status === 'approved';

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error('Please write a reason');
      return;
    }
    setSubmitting(true);
    try {
      await leaveService.cancel(leave._id, { reason: trimmed });
      toast.success('Leave cancelled');
      onConfirmed?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Cancel leave"
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={submitting}>Back</button>
          <button
            onClick={handleSubmit}
            className="btn-danger"
            disabled={submitting || !reason.trim()}
          >
            <X size={14} /> {submitting ? 'Cancelling...' : 'Cancel leave'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {wasApproved && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-amber-700 dark:text-amber-300">
              This leave was already approved. Admins will be notified about the cancellation.
            </div>
          </div>
        )}
        <div className="text-sm text-slate-600 dark:text-slate-300">
          You're cancelling your <b className="capitalize">{leave.type === 'short' ? 'short' : leave.type}</b> leave for{' '}
          <b>{fmtDate(leave.startDate)}</b>
          {leave.type !== 'short' && dayjs(leave.startDate).format('YYYY-MM-DD') !== dayjs(leave.endDate).format('YYYY-MM-DD') && (
            <> – <b>{fmtDate(leave.endDate)}</b></>
          )}
          {leave.type === 'short' && leave.startTime && leave.endTime && (
            <> ({fmtTime12(leave.startTime)} – {fmtTime12(leave.endTime)})</>
          )}
          .
        </div>
        <div>
          <label className="label">Reason for cancellation</label>
          <textarea
            rows={4}
            className="input"
            placeholder="Why are you cancelling this leave?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
          <p className="text-xs text-slate-400 mt-1">This reason will be visible to admins.</p>
        </div>
      </div>
    </Modal>
  );
}
